import type { SpaceLimit } from '../../hooks/useSpaceLimits';
import type { Space } from '../../lib/flotiq-client/types/api-response';

import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getEnv } from '../../lib/env';
import { checkoutPlanProperty } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import useToken from '../../hooks/useToken';

import { CaretRightIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Loader from '../Loader/Loader';

const AVAILABLE_TO_EXTEND = ['team_members_limit'];

const BuyMoreButton = ({
  detail,
  spaceId,
  planLimits,
  additionalClasses,
  testId,
}: {
  detail: SpaceLimit;
  spaceId: string;
  planLimits: Space['planLimits'];
  additionalClasses?: string;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const jwt = useToken();

  const [isBuying, setIsBuying] = useState<boolean>();

  const handleBuyExtra = async (propertyName: string) => {
    try {
      setIsBuying(true);

      const { body, status, ok } = await checkoutPlanProperty(jwt, undefined, {
        propertyName: propertyName,
        spaceId: spaceId,
        uri: `${
          getEnv('PUBLIC_URL') || window.origin
        }/organization?spaceId=${spaceId}&planId=${planLimits?.id}&planName=${
          planLimits?.name
        }`,
      });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      setIsBuying(false);

      window.location.assign(body.url);
    } catch {
      toast.error(t('Form.CommunicationErrorMessage'));
      setIsBuying(false);
    }
  };

  if (
    planLimits.price <= 0 ||
    detail.isUnlimited ||
    !AVAILABLE_TO_EXTEND.includes(detail.key)
  )
    return;

  return (
    <Button
      buttonColor="borderless"
      onClick={() => handleBuyExtra(detail?.key)}
      iconImage={<CaretRightIcon className="size-3" />}
      additionalClasses={additionalClasses}
      additionalChildrenClasses="flex flex-row items-center"
      {...getTestProps(testId, `buy-extra-${detail.key}`)}
    >
      {isBuying && (
        <Loader size="tiny" type="spinner-grid" additionalClasses="h-4 mr-1" />
      )}
      {t('Global.BuyMore')}
    </Button>
  );
};

export default BuyMoreButton;
